/**
 * The bubble animation catalogue: the single definition of what each animation *is*.
 *
 * Four surfaces render the same movement — the web widget SDK, the WhatsApp widget SDK, and the two
 * builder previews. Defining the keyframes once here is what stops a preview from validating
 * backwards, playing something production never plays.
 *
 * What this module deliberately does NOT contain: geometry, `position`, `z-index`, colour, or any
 * selector naming a bubble. It contributes movement and timing; each consumer attaches that movement
 * to whatever element it already owns. That is what keeps the builder preview inside its box instead
 * of pinned to the dashboard window.
 *
 * Parameters travel as CSS custom properties, so adding a knob costs nothing in CSS and the two SDKs
 * and the two previews stay byte-identical in behaviour.
 */

/** Movement duration. Slower reads as calmer, faster as more insistent. */
const DURATIONS = Object.freeze({
  slow: '1.2s',
  normal: '0.8s',
  fast: '0.5s',
});

/**
 * Multiplier applied inside the keyframes, so one set of keyframes covers all three intensities.
 * `subtle` is a hint, `strong` is hard to miss.
 */
const SCALES = Object.freeze({
  subtle: '0.4',
  normal: '1',
  strong: '1.6',
});

/** The catalogue. `none` is the absence of an animation, so it is not an entry. */
const ANIMATION_TYPES = Object.freeze(['bounce', 'pulse']);

const CLASS_PREFIX = 'cw-anim-';

const DEFAULT_SPEED = 'normal';
const DEFAULT_INTENSITY = 'normal';
const DEFAULT_DELAY = 0;
const DEFAULT_REPEAT_MODE = 'continuous';

/**
 * `bounce` lifts the bubble and lets it drop back faster than it rose, then rests. The rest is what
 * keeps continuous mode from reading as frantic. `pulse` swells and settles on the same rhythm.
 *
 * Both are driven by `--cw-anim-scale` through `calc()`, which is why intensity needs no second set
 * of keyframes. Both animate `transform` only — the one property that costs nothing to composite.
 */
export const BUBBLE_ANIMATION_CSS = `
@keyframes cw-anim-bounce {
  0% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0.33, 0, 0.15, 1);
  }
  35% {
    transform: translateY(calc(-10px * var(--cw-anim-scale, 1)));
    animation-timing-function: cubic-bezier(0.7, 0, 0.84, 0);
  }
  70%, 100% {
    transform: translateY(0);
  }
}

@keyframes cw-anim-pulse {
  0% {
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.33, 0, 0.15, 1);
  }
  30% {
    transform: scale(calc(1 + 0.06 * var(--cw-anim-scale, 1)));
    animation-timing-function: cubic-bezier(0.7, 0, 0.84, 0);
  }
  60%, 100% {
    transform: scale(1);
  }
}

.cw-anim-bounce,
.cw-anim-pulse {
  animation-duration: var(--cw-anim-duration, 0.8s);
  animation-delay: var(--cw-anim-delay, 0s);
  animation-iteration-count: var(--cw-anim-iterations, infinite);
}

.cw-anim-bounce {
  animation-name: cw-anim-bounce;
}

.cw-anim-pulse {
  animation-name: cw-anim-pulse;
}

@media (prefers-reduced-motion: reduce) {
  .cw-anim-bounce,
  .cw-anim-pulse {
    animation: none !important;
  }
}
`;

const isAnimated = animation =>
  Boolean(animation) && ANIMATION_TYPES.includes(animation.type);

/**
 * The class that plays `animation`, or `null` when there is nothing to play.
 *
 * A `null` column and `{ type: 'none' }` mean the same thing to every reader, which is why both
 * answer `null` here rather than each consumer testing for both shapes.
 */
export const resolveAnimationClass = animation =>
  isAnimated(animation) ? `${CLASS_PREFIX}${animation.type}` : null;

/**
 * The custom properties that tune `animation`, or `null` when there is nothing to play.
 *
 * Periodic mode is the exception in two places. Its iteration count is 1 because the scheduler — not
 * CSS — decides when the next play starts, and its delay is zero because the class is added and
 * removed repeatedly, so a CSS delay would fire again on every re-add and stack with the interval.
 * `applyAnimation` applies the configured delay to the first play instead.
 */
const resolveIterations = repeat => {
  const mode = repeat.mode || DEFAULT_REPEAT_MODE;

  if (mode === 'finite') return String(repeat.count);
  if (mode === 'periodic') return '1';
  return 'infinite';
};

export const resolveAnimationVars = animation => {
  if (!isAnimated(animation)) return null;

  const repeat = animation.repeat || {};
  const isPeriodic = repeat.mode === 'periodic';

  return {
    '--cw-anim-duration':
      DURATIONS[animation.speed] || DURATIONS[DEFAULT_SPEED],
    '--cw-anim-scale': SCALES[animation.intensity] || SCALES[DEFAULT_INTENSITY],
    '--cw-anim-delay': isPeriodic
      ? '0s'
      : `${Number(animation.delay) || DEFAULT_DELAY}s`,
    '--cw-anim-iterations': resolveIterations(repeat),
  };
};

const ANIMATION_LIFECYCLE_EVENTS = Object.freeze([
  'animationend',
  'animationcancel',
]);

/**
 * Whether `event` came from this catalogue class rather than from some other animation on the same
 * element.
 *
 * Animation events bubble, and a surface may run its own animations on the very node the attention
 * animation plays on — the WhatsApp launcher fades itself in on exactly that node. Each catalogue
 * class is named after its own keyframes, so the class name is the filter.
 */
const isOwnAnimation = (event, className) => event.animationName === className;

/**
 * Plays the run once and then takes the class off for good, returning the function that cancels it.
 *
 * "Repeat three times" means three plays per **page view** (US2), and a class left on the element
 * is a run that can start again. Both widgets suppress the animation while the chat panel is open
 * by dropping `animation-name`, which cancels the animation; leaving the class on would start a
 * fresh run of three every time the visitor closed the panel, forever. Listening for the cancel as
 * well as the end is what makes "and stops" true even when the visitor interrupts the run.
 */
const playFiniteAnimation = (element, className, clear) => {
  // Called with an event by the listeners, and without one by the returned stop function.
  const finish = event => {
    if (event && !isOwnAnimation(event, className)) return;

    ANIMATION_LIFECYCLE_EVENTS.forEach(type =>
      element.removeEventListener(type, finish)
    );
    clear();
  };

  ANIMATION_LIFECYCLE_EVENTS.forEach(type =>
    element.addEventListener(type, finish)
  );
  element.classList.add(className);

  return () => finish();
};

/**
 * Replays `className` on `element` every `repeat.interval` seconds, and returns the function that
 * stops it.
 *
 * Periodic mode is the one mode CSS cannot express. `@keyframes` percentages are fixed and cannot
 * read a custom property, so a "move, then rest" timeline cannot keep the movement duration
 * independent of the interval — at a 60s interval the same keyframes would stretch the bounce to
 * seconds long. Adding the class, removing it when the movement ends, and re-adding it after the
 * interval keeps the two independent.
 *
 * The class is added by the first timer rather than up front, which is what gives the configured
 * delay to the first play: `resolveAnimationVars` zeroes the CSS delay in this mode precisely
 * because a CSS delay would fire again on every re-add and stack with the interval.
 */
const schedulePeriodicAnimation = (element, animation, className, clear) => {
  const { interval } = animation.repeat;
  let timer = null;
  let stopped = false;

  const play = () => {
    if (stopped) return;
    element.classList.add(className);
  };

  const onAnimationEnd = event => {
    if (stopped || !isOwnAnimation(event, className)) return;
    element.classList.remove(className);
    timer = setTimeout(play, interval * 1000);
  };

  element.addEventListener('animationend', onAnimationEnd);
  timer = setTimeout(play, (Number(animation.delay) || DEFAULT_DELAY) * 1000);

  return () => {
    stopped = true;
    clearTimeout(timer);
    element.removeEventListener('animationend', onAnimationEnd);
    clear();
  };
};

/**
 * Plays `animation` on `element` — class plus custom properties — and returns the function that
 * stops it, leaving the element as it was found. This is the entry point every rendering surface
 * uses; nothing else applies the class.
 *
 * It exists so the three repetition modes are told apart in one place rather than in each of the
 * four surfaces. Continuous is the only one that is pure CSS. Finite has to be told when its run
 * is over, or it silently becomes "three plays every time the chat panel closes". Periodic must
 * *not* have the class applied up front at all, because the scheduler owns when each play starts —
 * a surface that pre-applied it would skip the configured first delay and then run two overlapping
 * schedules.
 *
 * The returned stop function is not optional for the caller: a surface that rebuilds its bubble
 * node — the WhatsApp launcher does, on every toggle — must stop the old schedule first or leak one
 * timer per rebuild. Calling it for a non-animated configuration is a safe no-op, so a caller can
 * apply and stop unconditionally.
 */
export const applyAnimation = (element, animation) => {
  const className = resolveAnimationClass(animation);

  if (!element || !className) return () => {};

  const vars = resolveAnimationVars(animation);
  Object.entries(vars).forEach(([property, value]) =>
    element.style.setProperty(property, value)
  );

  // Stopping restores the element rather than only silencing it: a surface that switches
  // configurations in place — the builder preview does — must not accumulate the tuning of every
  // choice the admin tried before this one.
  const clear = () => {
    element.classList.remove(className);
    Object.keys(vars).forEach(property =>
      element.style.removeProperty(property)
    );
  };

  const mode = animation.repeat?.mode;

  if (mode === 'periodic') {
    return schedulePeriodicAnimation(element, animation, className, clear);
  }

  if (mode === 'finite') {
    return playFiniteAnimation(element, className, clear);
  }

  element.classList.add(className);
  return clear;
};
